// v3/shared/components/LocationGuard.tsx — Blocks children until location is granted
//
// Hybrid enforcement:
// - Permissions API listener for real-time revocation (Chrome, Edge, Firefox)
// - Re-checks on route navigation as fallback (Safari)
//
// Renders inline within the layout — sidebar/header remain accessible.
// Modular: wrap around any route or component that requires location.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Loader2 } from 'lucide-react';
import { useLocationStore } from '../stores/location-store';

interface LocationGuardProps {
  children: React.ReactNode;
}

export function LocationGuard({ children }: LocationGuardProps) {
  const { status, hasListener, init, recheck } = useLocationStore();
  const location = useLocation();

  // Initialize on mount (sets up Permissions API listener if available)
  useEffect(() => {
    init();
  }, [init]);

  // Fallback: re-check on route navigation when no Permissions API listener
  useEffect(() => {
    if (!hasListener && status === 'granted') {
      recheck();
    }
  }, [location.pathname, hasListener, status, recheck]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3 text-center px-6">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Checking location access...</p>
        </div>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-4 text-center px-6 max-w-md">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--ch-danger-bg)]">
            <MapPin className="h-7 w-7 text-[var(--ch-danger-fg)]" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Location Access Required</h2>
          <p className="text-sm text-muted-foreground">
            This application requires location access to function. Please enable location permissions in your browser settings and reload the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
